import React from "react";
import { motion } from "framer-motion";
import { BookOpen, Award, TrendingUp } from "lucide-react";

const ITEM = {
    hidden: { opacity: 0, y: 14 },
    show: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 320, damping: 30 },
    },
};

function StatTile({ label, value, icon: Icon, accentText, testid }) {
    return (
        <motion.div
            variants={ITEM}
            whileHover={{ y: -3 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="group relative rounded-2xl bg-card border border-primary/10 p-6 overflow-hidden"
            data-testid={testid}
        >
            {/* Subtle gold accent arc */}
            <div
                aria-hidden
                className="absolute -top-10 -right-10 h-24 w-24 rounded-full bg-accent/[0.06] blur-xl"
            />
            <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                        {label}
                    </div>
                    <div className="font-display text-4xl text-primary mt-3 leading-none">
                        {value}
                    </div>
                    {accentText && (
                        <div className="mt-2 text-xs text-muted-foreground">
                            {accentText}
                        </div>
                    )}
                </div>
                <div className="h-10 w-10 rounded-xl bg-primary/5 ring-1 ring-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4.5 w-4.5 text-accent" />
                </div>
            </div>
        </motion.div>
    );
}

/**
 * StatSummaryBento — three compact stat tiles stacked vertically (on a bento
 * column) or side-by-side (on mobile when the hero is stacked). Kept
 * deliberately plain — the data viz lives in the Continue-Learning card.
 */
export default function StatSummaryBento({ enrollments = [] }) {
    const enrolledCount = enrollments.length;
    const certs = enrollments.filter((e) => e.certificate_id);
    const certCount = certs.length;
    const avg = enrolledCount
        ? Math.round(
              enrollments.reduce((s, e) => s + (e.progress_percent || 0), 0) /
                  enrolledCount,
          )
        : 0;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4" data-testid="stat-summary-bento">
            <StatTile
                label="Enrolled programs"
                value={enrolledCount}
                icon={BookOpen}
                accentText={
                    enrolledCount
                        ? `${enrollments.filter((e) => !e.completed_at).length} in progress`
                        : "Browse the catalog to start"
                }
                testid="stat-enrolled-programs"
            />
            <StatTile
                label="Certificates earned"
                value={certCount}
                icon={Award}
                accentText={
                    certCount
                        ? "Download them from the programs list"
                        : "Complete a program to earn your first"
                }
                testid="stat-certificates-earned"
            />
            <StatTile
                label="Average progress"
                value={`${avg}%`}
                icon={TrendingUp}
                accentText={
                    avg >= 75
                        ? "You're almost at the finish line"
                        : avg >= 40
                        ? "Solid pace — keep it up"
                        : enrolledCount
                        ? "Every lesson counts"
                        : "—"
                }
                testid="stat-avg-progress"
            />
        </div>
    );
}
